/* eslint-disable @typescript-eslint/no-use-before-define */
import { AppNavParams } from '@euler/app/Routes';
import { useAIAssistantForPage } from '@euler/app/components/ai/pageContext';
import { useNavigateToTaskListItem } from '@euler/app/flows/common/functions/useNavigateToTaskListItem';
import { CreateOrderFab } from '@euler/app/flows/home/components/CreateOrderFab';
import { DashboardHeader } from '@euler/app/flows/home/components/DashboardHeader';
import { TabContext } from '@euler/app/flows/home/functions/TabContext';
import { useDashboardHeader } from '@euler/app/flows/home/functions/useDashboardHeader';
import { useDashboardServiceRecords } from '@euler/app/flows/home/functions/useServiceRecords';
import { useTaskListLayout } from '@euler/app/flows/home/functions/useTaskListLayout';
import { useOrderContext } from '@euler/app/flows/order/functions/OrderContext';
import { useUserLocalSettings } from '@euler/app/flows/settings/functions/useUserLocalSettings';
import { useRefreshFeedback } from '@euler/functions/useRefreshFeedback';
import { wrapNavigatorScreen } from '@euler/functions/wrapNavigatorScreen';
import { TaskListItem } from '@euler/model/TaskListItem';
import { delayed, onErrorIgnore } from '@euler/utils';
import { usePersistFn, usePrevious } from '@euler/utils/hooks';
import {
  BottomTabNavigationOptions,
  useBottomTabBarHeight,
} from '@react-navigation/bottom-tabs';
import { useNavigation } from '@react-navigation/core';
import { StackNavigationProp } from '@react-navigation/stack';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { View } from 'react-native';
import { TaskList, TaskListRef } from '../components/TaskList';

export const HistoryScreen = wrapNavigatorScreen<
  Record<string, any>,
  BottomTabNavigationOptions
>(
  () => {
    useAIAssistantForPage('history_list');
    const navigation = useNavigation<StackNavigationProp<AppNavParams>>();
    const taskListRef = useRef<TaskListRef>(null);
    const tabHeight = useBottomTabBarHeight();
    const [userSettings] = useUserLocalSettings();
    const useV2Card = userSettings?.preferLegacyTaskCard !== true;
    const { width, numberOfColumns } = useTaskListLayout();
    const navigateToTaskItem = useNavigateToTaskListItem();

    const limit = numberOfColumns <= 1 ? 15 : numberOfColumns * 10;
    const playRefreshFeedback = useRefreshFeedback();
    const { event$ } = useOrderContext();

    const {
      segments,
      tags,
      segmentIndex,
      activeTag,
      assignedToMe,
      setSegmentIndex,
      setActiveTag,
    } = useDashboardHeader();

    const {
      data,
      status: { isLoading, isRefreshing, error, hasMore },
      refresh,
      update,
      fetchWithoutRefresh,
      fetchSinceLastError,
      onScrollToBottom,
    } = useDashboardServiceRecords({
      tag: activeTag,
      assignedToMe,
      limit,
    });

    const onRefresh = usePersistFn(() => {
      refresh()
        .then(() => {
          void playRefreshFeedback();
        })
        .catch(onErrorIgnore);
    });

    const onTaskPress = useCallback(
      (item: TaskListItem) => {
        navigateToTaskItem(item);
      },
      [navigateToTaskItem],
    );

    const onSearchPress = useCallback(() => {
      navigation.navigate('_search', {
        onPressResultItem: item => {
          console.log(item);
        },
      });
    }, [navigation]);

    const { scrollY } = useContext(TabContext);

    const prevSegmentIndex = usePrevious(segmentIndex);
    useEffect(() => {
      if (prevSegmentIndex != null && segmentIndex !== prevSegmentIndex) {
        onRefresh();
      }
    }, [segmentIndex, prevSegmentIndex, onRefresh]);

    const prevTag = usePrevious(activeTag);
    useEffect(() => {
      if (prevTag != activeTag) {
        onRefresh();
      }
    }, [activeTag, onRefresh, prevTag]);

    const stopScrollEvent = useRef(false);

    const handleScrollToBottom = usePersistFn(() => {
      if (hasMore) {
        stopScrollEvent.current = true;
      }
      delayed(() => {
        onScrollToBottom();
      }, 100);
    });

    useEffect(() => {
      stopScrollEvent.current = false;
    }, [data.length]);

    const onScrollOffsetChange = usePersistFn((offset: number) => {
      if (stopScrollEvent.current) return;
      scrollY.value = offset;
    });

    const updateSubscribeStatus = usePersistFn(
      (orderNo: string, subscribed: boolean) => {
        const index = data.findIndex(x => x.orderNo === orderNo);
        if (index >= 0) {
          update(item => {
            if (item.orderNo === orderNo) {
              item.subscribed = subscribed;
            }
            return undefined;
          });
        }
      },
    );

    // looking forward to catching finished tasks
    useEffect(() => {
      const sub = event$.subscribe(event => {
        if (!event.isMainOrder) return;
        if (event.type === 'rollback') {
          void fetchWithoutRefresh();
        } else if (event.type === 'finished') {
          taskListRef.current?.scrollToTop();
          void fetchWithoutRefresh();
        } else if (
          event.type == 'subscribed' ||
          event.type === 'unsubscribed'
        ) {
          updateSubscribeStatus(event.orderNo, event.type === 'subscribed');
        }
      });
      return () => sub.unsubscribe();
    }, [fetchWithoutRefresh, event$, updateSubscribeStatus]);

    useEffect(() => {
      delayed(onRefresh, 1000);
    }, [segments.length, onRefresh]);

    return (
      <View
        css={`
          flex: 1;
          background-color: #eee;
        `}
      >
        <DashboardHeader
          showSidebarTrigger
          segments={segments}
          tags={tags}
          activeTag={activeTag}
          defaultTitle="历史看板"
          onTagChange={setActiveTag}
          segmentIndex={segmentIndex}
          onSegmentIndexChange={setSegmentIndex}
          onSearchPress={onSearchPress}
          onRefresh={onRefresh}
        />
        <TaskList
          ref={taskListRef}
          width={width}
          columns={numberOfColumns}
          data={data}
          extraBottomSpacing={tabHeight}
          useV2Card={useV2Card}
          isRefreshing={isRefreshing}
          isLoading={isLoading}
          error={error}
          showFinishDate
          hideStatusLabel
          hasMore={hasMore}
          onRetry={fetchSinceLastError}
          onTaskPress={onTaskPress}
          onRefresh={onRefresh}
          onScrollToBottom={handleScrollToBottom}
          onScrollOffsetChange={onScrollOffsetChange}
          css={`
            flex: 1;
            align-self: stretch;
          `}
        />
        <CreateOrderFab />
      </View>
    );
  },
  {
    title: '历史看板',
    headerShown: false,
  },
);
